import React, { useState, useMemo, useEffect } from "react";
import { Container, Row, Col, Form, Button, Pagination } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import theme from "../../styles/theme/theme";
import ProductCard from "../../components/buyer/ProductCard";
import {
  fetchAllProducts,
  fetchSubcategories,
} from "../../features/buyer/buyerSlice";
import AppMessage from "../../components/common/AppMessage";
import Loader from "../../components/common/Loader";
import KycUploadModal from "../../components/buyer/ProfileOverview/KycUploadModal";
import KycBanner from "../../components/common/KycBanner";
// import "./styles.css";

import Breadcrumb from "../../components/Breadcrumb";
import { generateBreadcrumbs } from "../../utils/breadcrumbs";
import { IconButton } from "@mui/material";
import { Grid, List } from "lucide-react";

const PAGE_SIZE = 12;

const ProductList = () => {
  const dispatch = useDispatch();
  const {
    products,
    subcategories,
    productLoading: loading,
    productError: error,
  } = useSelector((state) => state.buyer);
  const productCategories = products?.data?.categories || [];
  const { token } = useSelector((state) => state.auth);
  const isLoggedIn = Boolean(token);

  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [viewType, setViewType] = useState(() => {
    return localStorage.getItem("viewType") || "grid";
  });
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    dispatch(fetchAllProducts()).unwrap();
  }, [dispatch]);

  useEffect(() => {
    if (category) {
      dispatch(fetchSubcategories(category)).unwrap();
    }
  }, [category, dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, category, subcategory, sortOrder]);

  useEffect(() => {
    localStorage.setItem("viewType", viewType);
  }, [viewType]);

  const filteredProducts = useMemo(() => {
    let filtered = products?.data?.products ? [...products.data.products] : [];
    if (searchTerm) {
      filtered = filtered.filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (category !== "") {
      filtered = filtered.filter((p) => p.category_id === Number(category));
    }
    if (subcategory !== "") {
      filtered = filtered.filter(
        (p) => p.subcategory_id === Number(subcategory)
      );
    }

    if (sortOrder) {
      filtered.sort((a, b) => {
        if (sortOrder === "price-asc") return a.price - b.price;
        if (sortOrder === "price-desc") return b.price - a.price;
        if (sortOrder === "name-asc") return a.name.localeCompare(b.name);
        if (sortOrder === "name-desc") return b.name.localeCompare(a.name);
        return 0;
      });
    }

    return filtered;
  }, [products, searchTerm, category, subcategory, sortOrder]);

  const totalPages = Math.ceil(filteredProducts.length / PAGE_SIZE);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const resetFilters = () => {
    setSearchTerm("");
    setCategory("");
    setSubcategory("");
    setSortOrder("");
    setCurrentPage(1);
  };

  const paginationItems = [];
  for (let number = 1; number <= totalPages; number++) {
    paginationItems.push(
      <Pagination.Item
        key={number}
        active={number === currentPage}
        onClick={() => setCurrentPage(number)}
      >
        {number}
      </Pagination.Item>
    );
  }

  // const handleViewDetails = (product) => {
  //   setSelectedProduct(product.id);
  // };

  const breadcrumbs = generateBreadcrumbs({ pageType: "products" });

  if (loading && !selectedProduct)
    return (
      <div
        style={{
          padding: theme.spacing(5),
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Loader />
      </div>
    );

  if (error)
    return (
      <AppMessage
        type="error"
        title="Available Products"
        message={error}
        showHomeButton
      />
    );

  return (
    <>
      <Container
        className="py-5"
        style={{
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* Filters and Search */}
        <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
          <Breadcrumb items={breadcrumbs} />
          <KycBanner />
        </Container>
        <div
          className="px-4 py-3"
          style={{
            zIndex: 1,
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            borderRadius: 10,
          }}
        >
          <h2 className="mb-3" style={{ color: theme.palette.tertiary.main }}>
            Available Products for Bidding
          </h2>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap mb-3"
            style={{ gap: "1rem" }}
          >
            <Form className="row g-2 align-items-center">
              <Col xs={12} sm={6} md="auto">
                <Form.Control
                  type="search"
                  placeholder="Search by product name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="modern-input shadow-sm"
                  style={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                    minWidth: "225px", // prevents cut-off
                    maxWidth: "400px", // prevents over-stretching
                  }}
                />
              </Col>

              <Col xs={12} sm={6} md="auto">
                <Form.Select
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setSubcategory("");
                  }}
                  className="modern-select shadow-sm"
                  style={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                  }}
                >
                  <option value="">Select Category</option>
                  {productCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} sm={6} md="auto">
                <Form.Select
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  disabled={!category}
                  className="modern-select shadow-sm"
                  style={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                  }}
                >
                  <option value="">Select Subcategory</option>
                  {subcategories.map((sub) => (
                    <option key={sub.id} value={sub.id}>
                      {sub.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} sm={6} md="auto">
                <Form.Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="modern-select shadow-sm"
                  style={{
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.text.primary,
                  }}
                >
                  <option value="">Select Sort</option>
                  <option value="price-asc">Price: Low to High</option>
                  <option value="price-desc">Price: High to Low</option>
                  <option value="name-asc">Name: A to Z</option>
                  <option value="name-desc">Name: Z to A</option>
                </Form.Select>
              </Col>
              <Col xs={12} md="auto">
                <Button
                  style={{
                    backgroundColor: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                  }}
                  onClick={resetFilters}
                  className="modern-button w-100"
                >
                  Reset Filters
                </Button>
              </Col>
            </Form>

            {/* Icons container */}
            <div className="ms-auto d-flex gap-2">
              <IconButton
                onClick={() => setViewType("grid")}
                style={{
                  backgroundColor:
                    viewType === "grid"
                      ? theme.palette.primary.main
                      : "transparent",
                  color:
                    viewType === "grid"
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                  borderRadius: 5,
                  transition: "all 0.3s ease",
                  transform: hovered === "grid" ? "scale(1.1)" : "scale(1)",
                  boxShadow:
                    hovered === "grid"
                      ? `0 0 10px ${theme.palette.primary.main}55`
                      : "none",
                }}
              >
                <Grid size={20} />
              </IconButton>
              <IconButton
                onClick={() => setViewType("list")}
                style={{
                  backgroundColor:
                    viewType === "list"
                      ? theme.palette.primary.main
                      : "transparent",
                  color:
                    viewType === "list"
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.primary,
                  borderRadius: 5,
                  transition: "all 0.3s ease",
                  transform: hovered === "list" ? "scale(1.1)" : "scale(1)",
                  boxShadow:
                    hovered === "list"
                      ? `0 0 10px ${theme.palette.primary.main}55`
                      : "none",
                }}
              >
                <List size={20} />
              </IconButton>
            </div>
          </div>
        </div>

        {/* Product List */}

        <div style={{ padding: "3rem 0" }}>
          {!loading && paginatedProducts.length > 0 ? (
            <Row className={viewType === "grid" ? "g-3" : "g-2 flex-column"}>
              {paginatedProducts.map((product) => (
                <Col
                  key={product.id}
                  xs={12}
                  sm={viewType === "grid" ? 6 : 12}
                  md={viewType === "grid" ? 6 : 12}
                  lg={viewType === "grid" ? 3 : 12}
                  className="d-flex justify-content-center"
                >
                  <ProductCard
                    product={product}
                    isLoggedIn={isLoggedIn}
                    viewType={viewType}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            !loading && (
              <AppMessage
                type="info"
                title="No products found."
                message="Try adjusting your filters or search keyword."
              />
            )
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && filteredProducts.length > 0 && (
          <div style={{ paddingTop: "2rem" }}>
            <Pagination className="mt-4 justify-content-center custom-pagination flex-wrap">
              <Pagination.Prev
                style={{ marginRight: 8 }}
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              />
              {paginationItems}
              <Pagination.Next
                style={{ marginRight: 8 }}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              />
            </Pagination>
            <div
              className="text-center mt-2"
              style={{ color: theme.palette.text.secondary }}
            >
              Page {currentPage} of {totalPages}
            </div>
          </div>
        )}
      </Container>
    </>
  );
};

export default ProductList;
